import React, { useEffect, useState } from 'react';

import {
  Grid,
  Box,
  IconButton,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
} from "@material-ui/core";
import { FileCopy } from '@material-ui/icons';
import { DragDropContext, Droppable, Draggable, OnDragEndResponder } from "react-beautiful-dnd";
import { useMutation } from "@tanstack/react-query";

import StorageProvider from '../../../framework/src/StorageProvider';

import { deletePaymentTerm } from './queries';
import { useStyles } from './styles';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';

type SelectedPackagesDraggableProps = {
  packages: any[];
  computePaymentTerm: (arg0: any) => any;
  onDragEnd: OnDragEndResponder;
  onPackageClick: (arg0: any, arg1: number) => void;
  onDeleteSuccess: () => void;
  onDuplicate: (arg: any) => void;
}

const SelectedPackagesDraggable = ({
  packages,
  computePaymentTerm,
  onDragEnd,
  onPackageClick,
  onDeleteSuccess,
  onDuplicate,
}: SelectedPackagesDraggableProps) => {
  const [token, setToken] = useState<string | null>(null);
  const classes = useStyles();

  useEffect(() => {
    const fetchToken = async () => {
      const storedToken = await StorageProvider.get("authToken");
      if (storedToken) {
        setToken(storedToken);
      }
    };

    fetchToken();
  }, []);
  
  const deleteMutation = useMutation({
    mutationFn: async (pkg: any) => {
      if (!token) {
        return;
      }

      let paymentTerm = computePaymentTerm({ data: pkg })[0];
      deletePaymentTerm(token, paymentTerm.paymentTermKey, pkg.id);
    },
    onSuccess: onDeleteSuccess,
  });

  const getTermName = (pkg: any, index: number) => {
    let paymentTerm = computePaymentTerm({ data: pkg })[index];
    if(!paymentTerm){
      paymentTerm = computePaymentTerm({ data: pkg })[0];
    }
    return paymentTerm.simpleValue;
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable
        droppableId={`payment-terms-list`}
        direction="horizontal"
      >
        {(provided) => (
          <Grid
            container
            ref={provided.innerRef}
            {...provided.droppableProps}
          >
            {packages.map((pkg, index) => (
              <Draggable
                key={index}
                draggableId={`payment-term-${index}`}
                index={index}
              >
                {(provided) => (

                  <Grid
                    item
                    xs={4}
                    className={classes.packageCardStyles}
                    onClick={() => onPackageClick(pkg, index)}
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                  >
                    <ListItem
                      classes={{
                        container: classes.listContainer,
                      }}
                      style={{paddingTop:'0px',paddingBottom:'0px'}}
                    >
                      <ListItemIcon style={{ minWidth: "0px" }}>
                        <DragIndicatorIcon style={{ color: "#ED1867" }} />
                      </ListItemIcon>
                      <ListItemText
                        classes={{
                          primary: classes.packageName,
                        }}
                        primary={`${pkg?.attributes?.name} ${pkg?.attributes?.selectedPaymentTerm?.simpleValue}`}
                        primaryTypographyProps={{ noWrap: true, style: { maxWidth: '95%', textOverflow: "ellipsis" } }}
                      />
                      <ListItemSecondaryAction
                        className={classes.actionsBox}
                      >
                        <IconButton
                          edge="end"
                          id="startRoadmap-deletepackagebtn"
                          onClick={(event) => {
                            event.stopPropagation();
                            deleteMutation.mutateAsync(pkg);
                          }}
                          aria-label="delete"
                          className={
                            classes.actionIconButton
                          }
                        >
                          <Box
                            className={classes.deleteButton}
                          >
                            X
                          </Box>
                        </IconButton>
                        <IconButton
                          edge="end"
                          id="startRoadmap-duplicatepackagebtn"
                          disabled={packages.length >= 3}
                          onClick={(event) => {
                            event.stopPropagation();
                            onDuplicate({ id: pkg.id, type: pkg.type});
                          }}
                          className={classes.actionIconButton}
                        >
                          <FileCopy className={classes.duplicateButton} />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </Grid>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </Grid>
        )}
      </Droppable>
    </DragDropContext>
  )
};

export default SelectedPackagesDraggable;